import React, { useEffect, useMemo, useRef, useState } from "react";

const FUNCTION_BASE_URL =
  "https://us-central1-xeroo-farmacias-moscovich.cloudfunctions.net";

const ENDPOINTS = {
  createMeeting: `${FUNCTION_BASE_URL}/createMeeting`,
  startMeeting: `${FUNCTION_BASE_URL}/startMeeting`,
  realtimeSession: `${FUNCTION_BASE_URL}/createRealtimeTranscriptionSession`,
  appendTranscript: `${FUNCTION_BASE_URL}/appendMeetingTranscript`,
  analyze: `${FUNCTION_BASE_URL}/analyzeMeetingIncremental`,
  completeSuggestion: `${FUNCTION_BASE_URL}/completeMeetingSuggestion`,
  finishMeeting: `${FUNCTION_BASE_URL}/finishMeeting`,
  getMeeting: `${FUNCTION_BASE_URL}/getMeeting`,
};

const TRANSCRIPTION_USD_PER_MINUTE = 0.017;
const AI_INPUT_USD_PER_1M = 5;
const AI_OUTPUT_USD_PER_1M = 30;

export default function MeetingTranscriber() {
  const [meetingId, setMeetingId] = useState("");
  const [status, setStatus] = useState("idle");
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState("");

  const [meetingSeconds, setMeetingSeconds] = useState(0);
  const [aiCostUsd, setAiCostUsd] = useState(0);
  const [importantNotes, setImportantNotes] = useState("");

  const [form, setForm] = useState({
    title: "",
    clientName: "",
    projectName: "",
    clientContext: "",
    projectContext: "",
    expectedGoal: "",
    currentSystems: "",
    possibleIntegrations: "",
    notes: "",
  });

  const [partialText, setPartialText] = useState("");
  const [finalText, setFinalText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [meetingResult, setMeetingResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const streamRef = useRef(null);

  const pendingFinalBufferRef = useRef("");
  const livePartialRef = useRef("");
  const finalTextRef = useRef("");

  const analyzeIntervalRef = useRef(null);
  const autoFlushIntervalRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  const transcriptionCostUsd = useMemo(() => {
    return (meetingSeconds / 60) * TRANSCRIPTION_USD_PER_MINUTE;
  }, [meetingSeconds]);

  const totalCostUsd = transcriptionCostUsd + aiCostUsd;

  useEffect(() => {
    if (!isConnected) return;

    const timer = setInterval(() => {
      setMeetingSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isConnected]);

  function updateForm(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function setFinalTextSafe(updater) {
    setFinalText((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      finalTextRef.current = next;
      return next;
    });
  }

  async function postJson(url, body = {}) {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || data.ok === false) {
      throw new Error(JSON.stringify(data, null, 2));
    }

    return data;
  }

  async function createMeetingIfNeeded() {
    if (meetingId) return meetingId;

    setStatus("creating_meeting");

    const data = await postJson(ENDPOINTS.createMeeting, {
      ...form,
      importantNotes,
    });

    setMeetingId(data.meetingId);
    return data.meetingId;
  }

  async function startTranscription() {
    try {
      setError("");
      setMeetingResult(null);

      const id = await createMeetingIfNeeded();

      await postJson(ENDPOINTS.startMeeting, { meetingId: id });

      setStatus("creating_realtime_session");

      const sessionData = await postJson(ENDPOINTS.realtimeSession, {
        language: "es",
      });

      const ephemeralKey =
        sessionData?.client_secret?.value || sessionData?.value;

      if (!ephemeralKey) throw new Error("No se recibió ephemeral key");

      setStatus("requesting_microphone");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      setStatus("connecting_webrtc");

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;

      dc.onopen = () => {
        setIsConnected(true);
        setStatus("active");
        startAutoAnalysis(id);
        startAutoFlush(id);
      };

      dc.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          if (
            data.type ===
            "conversation.item.input_audio_transcription.delta"
          ) {
            const delta = data.delta || "";
            livePartialRef.current += delta;
            setPartialText(livePartialRef.current);
          }

          if (
            data.type ===
            "conversation.item.input_audio_transcription.completed"
          ) {
            const transcript = String(data.transcript || "").trim();

            if (transcript) {
              setFinalTextSafe((prev) => prev + transcript + "\n");
              pendingFinalBufferRef.current += transcript + "\n";
              scheduleSaveTranscript(id);
            }

            livePartialRef.current = "";
            setPartialText("");
          }

          if (data.type === "error") {
            console.error("Realtime error:", data);
            setError(JSON.stringify(data.error || data, null, 2));
          }
        } catch (err) {
          console.error("DataChannel message error", err);
        }
      };

      dc.onerror = () => setError("Error en canal realtime");

      pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === "failed" ||
          pc.connectionState === "disconnected" ||
          pc.connectionState === "closed"
        ) {
          setIsConnected(false);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpRes = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          "Content-Type": "application/sdp",
        },
        body: offer.sdp,
      });

      if (!sdpRes.ok) throw new Error(await sdpRes.text());

      const answerSdp = await sdpRes.text();

      await pc.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });
    } catch (err) {
      console.error(err);
      setError(err.message || "Error desconocido");
      setStatus("error");
      stopTranscription(false);
    }
  }

  function scheduleSaveTranscript(id) {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(async () => {
      await flushTranscript(id);
    }, 1500);
  }

  function startAutoFlush(id) {
    if (autoFlushIntervalRef.current) clearInterval(autoFlushIntervalRef.current);

    autoFlushIntervalRef.current = setInterval(() => {
      flushTranscript(id);
    }, 15000);
  }

  async function flushTranscript(id = meetingId) {
    let text = pendingFinalBufferRef.current.trim();

    if (!text && livePartialRef.current.trim()) {
      text = livePartialRef.current.trim();

      livePartialRef.current = "";
      setPartialText("");

      setFinalTextSafe((prev) => {
        if (prev.includes(text)) return prev;
        return prev + text + "\n";
      });
    }

    if (!id || !text) return;

    pendingFinalBufferRef.current = "";

    try {
      await postJson(ENDPOINTS.appendTranscript, {
        meetingId: id,
        text,
        source: "final",
      });
    } catch (err) {
      console.error("Error saving transcript", err);
      pendingFinalBufferRef.current = text + "\n" + pendingFinalBufferRef.current;
      setError("No se pudo guardar una parte de la transcripción.");
    }
  }

  function estimateAiCost({ inputText = "", expectedOutputTokens = 1200 }) {
    const inputTokens = Math.ceil(inputText.length / 4);
    const inputCost = (inputTokens / 1_000_000) * AI_INPUT_USD_PER_1M;
    const outputCost = (expectedOutputTokens / 1_000_000) * AI_OUTPUT_USD_PER_1M;
    return inputCost + outputCost;
  }

  function startAutoAnalysis(id) {
    if (analyzeIntervalRef.current) clearInterval(analyzeIntervalRef.current);

    analyzeIntervalRef.current = setInterval(() => {
      analyzeMeeting(id);
    }, 120000);
  }

  async function analyzeMeeting(id = meetingId) {
    if (!id || isAnalyzing) return;

    try {
      setIsAnalyzing(true);
      await flushTranscript(id);

      const estimatedCost = estimateAiCost({
        inputText: finalTextRef.current + importantNotes,
        expectedOutputTokens: 900,
      });

      await postJson(ENDPOINTS.analyze, {
        meetingId: id,
        importantNotes,
      });

      setAiCostUsd((prev) => prev + estimatedCost);

      await refreshMeeting(id);
    } catch (err) {
      console.error(err);
      setError("No se pudo analizar la reunión con IA.");
    } finally {
      setIsAnalyzing(false);
    }
  }

  async function refreshMeeting(id = meetingId) {
    if (!id) return;

    const url = `${ENDPOINTS.getMeeting}?meetingId=${encodeURIComponent(id)}`;
    const res = await fetch(url);
    const data = await res.json();

    if (!res.ok || data.ok === false) {
      throw new Error(JSON.stringify(data, null, 2));
    }

    setSuggestions(data.suggestions || []);

    if (data.meeting?.fullTranscript && !finalTextRef.current) {
      setFinalTextSafe(data.meeting.fullTranscript);
    }

    if (data.meeting?.summary) {
      setMeetingResult(data.meeting.summary);
    }
  }

  async function toggleSuggestionCompleted(suggestion) {
    try {
      await postJson(ENDPOINTS.completeSuggestion, {
        meetingId,
        suggestionId: suggestion.id,
        completed: !suggestion.completed,
      });

      await refreshMeeting(meetingId);
    } catch (err) {
      console.error(err);
      setError("No se pudo actualizar la sugerencia.");
    }
  }

  async function stopTranscription(markStatus = true) {
    try {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      if (analyzeIntervalRef.current) {
        clearInterval(analyzeIntervalRef.current);
        analyzeIntervalRef.current = null;
      }

      if (autoFlushIntervalRef.current) {
        clearInterval(autoFlushIntervalRef.current);
        autoFlushIntervalRef.current = null;
      }

      dcRef.current?.close();
      pcRef.current?.close();

      streamRef.current?.getTracks().forEach((track) => track.stop());

      await flushTranscript(meetingId);
    } catch (err) {
      console.error(err);
    }

    dcRef.current = null;
    pcRef.current = null;
    streamRef.current = null;

    setIsConnected(false);

    if (markStatus) setStatus("paused");
  }

  async function finishMeeting() {
    try {
      if (!meetingId) return;

      setIsFinishing(true);
      setError("");

      await stopTranscription(false);
      await flushTranscript(meetingId);

      const estimatedCost = estimateAiCost({
        inputText: finalTextRef.current + importantNotes,
        expectedOutputTokens: 3500,
      });

      const data = await postJson(ENDPOINTS.finishMeeting, {
        meetingId,
        importantNotes,
      });

      setAiCostUsd((prev) => prev + estimatedCost);
      setMeetingResult(data.result);
      setStatus("finished");

      await refreshMeeting(meetingId);
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo finalizar la reunión");
    } finally {
      setIsFinishing(false);
    }
  }

  function copyText(text) {
    navigator.clipboard.writeText(text || "");
  }

  useEffect(() => {
    return () => {
      try {
        if (analyzeIntervalRef.current) clearInterval(analyzeIntervalRef.current);
        if (autoFlushIntervalRef.current)
          clearInterval(autoFlushIntervalRef.current);
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

        dcRef.current?.close();
        pcRef.current?.close();
        streamRef.current?.getTracks().forEach((track) => track.stop());
      } catch (_) {}
    };
  }, []);

  const canEditContext = !isConnected && status !== "finished";

  return (
    <div style={styles.appShell}>
      <aside style={styles.sidebar}>
        <div>
          <div style={styles.logoRow}>
            <div style={styles.logoMark}>◎</div>
            <div style={styles.logoText}>Nexus</div>
          </div>

          <nav style={styles.nav}>
            <div style={styles.navItemActive}>
              <span style={styles.navIcon}>▦</span>
              <span>Reunión IA</span>
            </div>
            <div style={styles.navItem}>
              <span style={styles.navIcon}>☰</span>
              <span>Transcripción</span>
            </div>
            <div style={styles.navItem}>
              <span style={styles.navIcon}>✦</span>
              <span>Minuta</span>
            </div>
          </nav>
        </div>

        <div style={styles.userBox}>
          <div style={styles.avatar}>AK</div>
          <div>
            <div style={styles.userName}>Alex Kravchuk</div>
            <div style={styles.userMail}>alexk@xeroo.ai</div>
          </div>
        </div>
      </aside>

      <main style={styles.main}>
        <div style={styles.topBar}>
          <div style={styles.searchBox}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              style={styles.searchInput}
              placeholder="Buscar reuniones, clientes o proyectos..."
              disabled
            />
          </div>

          <button style={styles.newButton} onClick={startTranscription}>
            + {status === "paused" ? "Continuar" : "Nueva Reunión"}
          </button>
        </div>

        <header style={styles.header}>
          <div>
            <h1 style={styles.title}>Gestión de reuniones con IA</h1>
            <p style={styles.subtitle}>
              Transcripción realtime, sugerencias en vivo, minuta final y prompt
              para crear una app a medida.
            </p>
          </div>

          <div style={isConnected ? styles.liveBadge : styles.statusBadge}>
            <span style={isConnected ? styles.liveDot : styles.statusDot} />
            {isConnected ? "Grabando" : status}
          </div>
        </header>

        <div style={styles.metricsGrid}>
          <MetricCard
            icon="⏱"
            tone="blue"
            label="Tiempo reunión"
            value={formatDuration(meetingSeconds)}
            sub="Tiempo activo"
            trend={isConnected ? "LIVE" : "PAUSE"}
          />
          <MetricCard
            icon="$"
            tone="orange"
            label="Transcripción"
            value={`USD ${transcriptionCostUsd.toFixed(4)}`}
            sub="Estimado realtime"
            trend="+0.017/min"
          />
          <MetricCard
            icon="✦"
            tone="green"
            label="Procesamiento IA"
            value={`USD ${aiCostUsd.toFixed(4)}`}
            sub="Análisis + minuta"
            trend="estimado"
          />
          <MetricCard
            icon="◎"
            tone="red"
            label="Total estimado"
            value={`USD ${totalCostUsd.toFixed(4)}`}
            sub="Costo API"
            trend="total"
          />
        </div>

        {error && (
          <div style={styles.errorBox}>
            <strong>Error:</strong>
            <pre style={styles.errorPre}>{error}</pre>
          </div>
        )}

        <div style={styles.contentGrid}>
          <section style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Contexto del cliente y proyecto</h2>
              {meetingId && <span style={styles.smallPill}>ID {meetingId}</span>}
            </div>

            <div style={styles.twoCols}>
              <Input
                label="Título de reunión"
                value={form.title}
                disabled={!canEditContext}
                onChange={(v) => updateForm("title", v)}
                placeholder="Ej: Discovery CRM para vendedores"
              />

              <Input
                label="Cliente / empresa"
                value={form.clientName}
                disabled={!canEditContext}
                onChange={(v) => updateForm("clientName", v)}
                placeholder="Ej: Grupo Portland"
              />

              <Input
                label="Nombre del proyecto"
                value={form.projectName}
                disabled={!canEditContext}
                onChange={(v) => updateForm("projectName", v)}
                placeholder="Ej: CRM comercial a medida"
              />

              <Input
                label="Objetivo esperado"
                value={form.expectedGoal}
                disabled={!canEditContext}
                onChange={(v) => updateForm("expectedGoal", v)}
                placeholder="Ej: Mejorar seguimiento comercial"
              />
            </div>

            <Textarea
              label="Contexto del cliente"
              value={form.clientContext}
              disabled={!canEditContext}
              onChange={(v) => updateForm("clientContext", v)}
              placeholder="Rubro, tamaño, operación, equipos..."
            />

            <Textarea
              label="Contexto del proyecto"
              value={form.projectContext}
              disabled={!canEditContext}
              onChange={(v) => updateForm("projectContext", v)}
              placeholder="Qué se quiere construir, problema actual, procesos..."
            />

            <div style={styles.twoCols}>
              <Textarea
                label="Sistemas actuales"
                value={form.currentSystems}
                disabled={!canEditContext}
                onChange={(v) => updateForm("currentSystems", v)}
                placeholder="HubSpot, Excel, ERP, WhatsApp..."
              />

              <Textarea
                label="Integraciones posibles"
                value={form.possibleIntegrations}
                disabled={!canEditContext}
                onChange={(v) => updateForm("possibleIntegrations", v)}
                placeholder="WhatsApp, email, calendario, facturación..."
              />
            </div>

            <Textarea
              label="Notas previas"
              value={form.notes}
              disabled={!canEditContext}
              onChange={(v) => updateForm("notes", v)}
              placeholder="Todo dato previo que ayude a la IA..."
            />
          </section>

          <section style={styles.sideCard}>
            <h2 style={styles.sectionLabel}>Notas importantes</h2>
            <div style={styles.bigStat}>Prioridad</div>
            <p style={styles.cardHint}>
              Escribí acá lo que querés que la IA tenga especialmente en cuenta.
            </p>
            <textarea
              style={styles.importantNotes}
              value={importantNotes}
              onChange={(e) => setImportantNotes(e.target.value)}
              placeholder="Ej: El cliente busca bajar costos de HubSpot. Priorizar WhatsApp, tareas automáticas, reportes por vendedor y migración simple..."
            />

            <div style={styles.tipBox}>
              Sugerencia: cargá acá presupuesto, urgencias, prioridades y
              decisiones clave para mejorar la minuta final.
            </div>
          </section>
        </div>

        <div style={styles.contentGrid}>
          <section style={styles.card}>
            <div style={styles.actions}>
              {!isConnected ? (
                <button style={styles.primaryButton} onClick={startTranscription}>
                  {status === "paused"
                    ? "Continuar transcripción"
                    : "Iniciar reunión"}
                </button>
              ) : (
                <button
                  style={styles.warningButton}
                  onClick={() => stopTranscription(true)}
                >
                  Pausar transcripción
                </button>
              )}

              <button
                style={styles.secondaryButton}
                disabled={!meetingId || isAnalyzing}
                onClick={() => analyzeMeeting(meetingId)}
              >
                {isAnalyzing ? "Analizando..." : "Analizar ahora"}
              </button>

              <button
                style={styles.dangerButton}
                disabled={!meetingId || isFinishing}
                onClick={finishMeeting}
              >
                {isFinishing ? "Finalizando..." : "Finalizar reunión"}
              </button>
            </div>

            <h2 style={styles.cardTitle}>Transcripción final</h2>
            <div style={styles.transcriptBox}>
              {finalText ||
                "Cuando empieces a hablar, la transcripción final aparecerá acá..."}
            </div>

            <h2 style={styles.cardTitle}>Transcripción parcial</h2>
            <div style={styles.partialBox}>
              {partialText || "El texto parcial aparece en vivo mientras hablás..."}
            </div>
          </section>

          <section style={styles.sideCard}>
            <div style={styles.cardHeader}>
              <h2 style={styles.sectionLabel}>Sugerencias IA</h2>
              <button
                style={styles.smallButton}
                disabled={!meetingId}
                onClick={() => refreshMeeting(meetingId)}
              >
                Refrescar
              </button>
            </div>

            {suggestions.length === 0 ? (
              <div style={styles.empty}>
                Todavía no hay sugerencias. Se generarán durante la reunión.
              </div>
            ) : (
              <div style={styles.suggestionList}>
                {suggestions.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      ...styles.suggestionCard,
                      opacity: s.completed ? 0.55 : 1,
                    }}
                  >
                    <div style={styles.suggestionTop}>
                      <span style={styles.badge}>{s.type}</span>
                      <span style={styles.priority}>{s.priority}</span>
                    </div>

                    <h3 style={styles.suggestionTitle}>{s.title}</h3>
                    <p style={styles.suggestionDetail}>{s.detail}</p>

                    <button
                      style={
                        s.completed ? styles.smallButton : styles.completeButton
                      }
                      onClick={() => toggleSuggestionCompleted(s)}
                    >
                      {s.completed ? "Marcar pendiente" : "Marcar completada"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <section style={styles.card}>
          <h2 style={styles.cardTitle}>Resultado final</h2>

          {!meetingResult ? (
            <div style={styles.empty}>
              Al finalizar se generará la minuta, próximos pasos y prompt de app.
            </div>
          ) : (
            <div style={styles.resultGrid}>
              <ResultBlock
                title="Resumen ejecutivo"
                text={meetingResult.executiveSummary}
                onCopy={() => copyText(meetingResult.executiveSummary)}
              />

              <ResultBlock
                title="Minuta"
                text={meetingResult.meetingMinutes}
                onCopy={() => copyText(meetingResult.meetingMinutes)}
              />

              <ListBlock title="Próximos pasos" items={meetingResult.nextSteps} />

              <ListBlock
                title="Requerimientos funcionales"
                items={meetingResult.functionalRequirements}
              />

              <ListBlock title="Integraciones" items={meetingResult.integrations} />

              <ListBlock
                title="Riesgos / dudas pendientes"
                items={meetingResult.risksAndPendingQuestions}
              />

              <ResultBlock
                title="Prompt para crear app"
                text={meetingResult.appPrompt}
                onCopy={() => copyText(meetingResult.appPrompt)}
                wide
              />
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function MetricCard({ icon, tone, label, value, sub, trend }) {
  const iconStyle = {
    ...styles.metricIcon,
    ...(tone === "blue" ? styles.metricBlue : {}),
    ...(tone === "orange" ? styles.metricOrange : {}),
    ...(tone === "green" ? styles.metricGreen : {}),
    ...(tone === "red" ? styles.metricRed : {}),
  };

  return (
    <div style={styles.metricCard}>
      <div style={styles.metricTop}>
        <div style={iconStyle}>{icon}</div>
        <div style={styles.trend}>{trend}</div>
      </div>

      <div style={styles.metricLabel}>{label}</div>
      <div style={styles.metricValue}>{value}</div>
      <div style={styles.metricSub}>{sub}</div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, disabled }) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>
      <input
        style={styles.input}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function Textarea({ label, value, onChange, placeholder, disabled }) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>
      <textarea
        style={styles.textarea}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function ResultBlock({ title, text, onCopy, wide }) {
  return (
    <div style={wide ? styles.resultBlockWide : styles.resultBlock}>
      <div style={styles.resultHeader}>
        <h3 style={styles.resultTitle}>{title}</h3>
        <button style={styles.smallButton} onClick={onCopy}>
          Copiar
        </button>
      </div>
      <div style={styles.resultText}>{text || "-"}</div>
    </div>
  );
}

function ListBlock({ title, items }) {
  return (
    <div style={styles.resultBlock}>
      <h3 style={styles.resultTitle}>{title}</h3>
      {!items || items.length === 0 ? (
        <div style={styles.emptySmall}>Sin datos</div>
      ) : (
        <ul style={styles.ul}>
          {items.map((item, idx) => (
            <li key={idx} style={styles.li}>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  return [
    h > 0 ? String(h).padStart(2, "0") : null,
    String(m).padStart(2, "0"),
    String(s).padStart(2, "0"),
  ]
    .filter(Boolean)
    .join(":");
}

const styles = {
  appShell: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "270px 1fr",
    background: "#f7f9fc",
    color: "#0f172a",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Arial, sans-serif",
  },
  sidebar: {
    margin: 24,
    borderRadius: 28,
    background: "#ffffff",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.08)",
    border: "1px solid #eef2f7",
    padding: 28,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "calc(100vh - 48px)",
    position: "sticky",
    top: 24,
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 42,
  },
  logoMark: {
    width: 28,
    height: 28,
    borderRadius: 999,
    color: "#5536f2",
    fontSize: 27,
    lineHeight: "25px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
  },
  logoText: {
    fontSize: 24,
    fontWeight: 900,
    color: "#0f172a",
    letterSpacing: -0.7,
  },
  nav: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  navItemActive: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    background: "linear-gradient(135deg, #6236ff, #4f33e8)",
    color: "#fff",
    padding: "16px 18px",
    borderRadius: 16,
    fontWeight: 800,
    boxShadow: "0 14px 28px rgba(83, 55, 242, 0.30)",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    color: "#64748b",
    padding: "16px 18px",
    borderRadius: 16,
    fontWeight: 800,
  },
  navIcon: {
    width: 26,
    textAlign: "center",
    fontSize: 20,
  },
  userBox: {
    borderTop: "1px solid #edf2f7",
    paddingTop: 24,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #5536f2, #82a5ff)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: 13,
  },
  userName: {
    fontWeight: 900,
    fontSize: 14,
  },
  userMail: {
    color: "#64748b",
    fontSize: 12,
    marginTop: 2,
  },
  main: {
    padding: "24px 30px 40px 14px",
    overflow: "hidden",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
    marginBottom: 28,
  },
  searchBox: {
    width: 640,
    height: 52,
    borderRadius: 18,
    background: "#ffffff",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.07)",
    border: "1px solid #edf2f7",
    display: "flex",
    alignItems: "center",
    padding: "0 18px",
    gap: 12,
  },
  searchIcon: {
    color: "#8aa0bf",
    fontSize: 20,
  },
  searchInput: {
    border: "none",
    outline: "none",
    flex: 1,
    fontSize: 15,
    background: "transparent",
    color: "#64748b",
    fontWeight: 600,
  },
  newButton: {
    border: "none",
    background: "linear-gradient(135deg, #6236ff, #4f33e8)",
    color: "#fff",
    borderRadius: 14,
    padding: "14px 22px",
    fontWeight: 900,
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(83, 55, 242, 0.28)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,
    marginBottom: 28,
  },
  title: {
    margin: 0,
    fontSize: 35,
    lineHeight: 1.05,
    color: "#0f172a",
    fontWeight: 950,
    letterSpacing: -1.4,
  },
  subtitle: {
    margin: "10px 0 0",
    color: "#536987",
    fontSize: 17,
    fontWeight: 600,
  },
  statusBadge: {
    background: "#fff",
    border: "1px solid #edf2f7",
    color: "#64748b",
    borderRadius: 999,
    padding: "12px 16px",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 900,
  },
  liveBadge: {
    background: "#ecfdf5",
    border: "1px solid #bbf7d0",
    color: "#047857",
    borderRadius: 999,
    padding: "12px 16px",
    boxShadow: "0 10px 24px rgba(16, 185, 129, 0.12)",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 900,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#94a3b8",
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#10b981",
    boxShadow: "0 0 0 5px rgba(16, 185, 129, 0.12)",
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 24,
    marginBottom: 28,
  },
  metricCard: {
    background: "#ffffff",
    borderRadius: 24,
    padding: 28,
    minHeight: 128,
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.07)",
    border: "1px solid #edf2f7",
  },
  metricTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 22,
  },
  metricIcon: {
    width: 44,
    height: 44,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: 18,
  },
  metricBlue: {
    background: "#eef2ff",
    color: "#5536f2",
  },
  metricOrange: {
    background: "#fff7ed",
    color: "#ea580c",
  },
  metricGreen: {
    background: "#ecfdf5",
    color: "#059669",
  },
  metricRed: {
    background: "#fff1f2",
    color: "#e11d48",
  },
  trend: {
    background: "#ecfdf5",
    color: "#059669",
    borderRadius: 999,
    padding: "6px 9px",
    fontSize: 11,
    fontWeight: 900,
  },
  metricLabel: {
    color: "#8aa0bf",
    fontSize: 13,
    textTransform: "uppercase",
    fontWeight: 950,
    letterSpacing: 1.6,
  },
  metricValue: {
    marginTop: 8,
    color: "#0f172a",
    fontSize: 30,
    fontWeight: 950,
    letterSpacing: -0.9,
  },
  metricSub: {
    color: "#8aa0bf",
    marginTop: 4,
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "2fr 0.95fr",
    gap: 24,
    marginBottom: 28,
    alignItems: "start",
  },
  card: {
    background: "#ffffff",
    borderRadius: 26,
    padding: 28,
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.07)",
    border: "1px solid #edf2f7",
  },
  sideCard: {
    background: "#ffffff",
    borderRadius: 26,
    padding: 28,
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.07)",
    border: "1px solid #edf2f7",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 18,
  },
  cardTitle: {
    margin: "0 0 18px",
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 950,
    letterSpacing: -0.5,
  },
  sectionLabel: {
    margin: 0,
    color: "#8aa0bf",
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: 2,
    fontWeight: 950,
  },
  bigStat: {
    fontSize: 40,
    color: "#0f172a",
    fontWeight: 950,
    letterSpacing: -1.4,
    marginTop: 24,
  },
  cardHint: {
    color: "#8aa0bf",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.5,
    marginBottom: 14,
  },
  smallPill: {
    background: "#f1f5f9",
    color: "#64748b",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 900,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  twoCols: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
    marginBottom: 14,
  },
  label: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 900,
  },
  input: {
    height: 46,
    border: "1px solid #e6edf5",
    background: "#f8fafc",
    borderRadius: 14,
    padding: "0 14px",
    outline: "none",
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 650,
  },
  textarea: {
    minHeight: 82,
    border: "1px solid #e6edf5",
    background: "#f8fafc",
    borderRadius: 14,
    padding: 14,
    outline: "none",
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "inherit",
    resize: "vertical",
  },
  importantNotes: {
    width: "100%",
    minHeight: 170,
    boxSizing: "border-box",
    border: "1px solid #e6edf5",
    background: "#f8fafc",
    borderRadius: 18,
    padding: 16,
    outline: "none",
    color: "#0f172a",
    fontSize: 14,
    lineHeight: 1.55,
    fontWeight: 650,
    fontFamily: "inherit",
    resize: "vertical",
  },
  tipBox: {
    marginTop: 20,
    background: "#eef2ff",
    color: "#4338ca",
    borderRadius: 18,
    padding: 18,
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 850,
    textTransform: "uppercase",
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 24,
  },
  primaryButton: {
    border: "none",
    background: "linear-gradient(135deg, #6236ff, #4f33e8)",
    color: "#fff",
    borderRadius: 14,
    padding: "13px 18px",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 14px 28px rgba(83, 55, 242, 0.25)",
  },
  secondaryButton: {
    border: "1px solid #e6edf5",
    background: "#ffffff",
    color: "#536987",
    borderRadius: 14,
    padding: "13px 18px",
    fontWeight: 950,
    cursor: "pointer",
  },
  warningButton: {
    border: "none",
    background: "#f59e0b",
    color: "#fff",
    borderRadius: 14,
    padding: "13px 18px",
    fontWeight: 950,
    cursor: "pointer",
  },
  dangerButton: {
    border: "none",
    background: "#ef4444",
    color: "#fff",
    borderRadius: 14,
    padding: "13px 18px",
    fontWeight: 950,
    cursor: "pointer",
  },
  smallButton: {
    border: "1px solid #e6edf5",
    background: "#ffffff",
    color: "#536987",
    borderRadius: 12,
    padding: "8px 12px",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 12,
  },
  completeButton: {
    border: "none",
    background: "#10b981",
    color: "#fff",
    borderRadius: 12,
    padding: "8px 12px",
    fontWeight: 900,
    cursor: "pointer",
    fontSize: 12,
  },
  transcriptBox: {
    minHeight: 260,
    maxHeight: 420,
    overflow: "auto",
    background: "#f8fafc",
    border: "1px solid #e6edf5",
    borderRadius: 20,
    padding: 18,
    whiteSpace: "pre-wrap",
    lineHeight: 1.65,
    color: "#334155",
    marginBottom: 22,
    fontWeight: 600,
  },
  partialBox: {
    minHeight: 110,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 20,
    padding: 18,
    whiteSpace: "pre-wrap",
    lineHeight: 1.65,
    color: "#9a3412",
    fontWeight: 700,
  },
  suggestionList: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  suggestionCard: {
    border: "1px solid #e6edf5",
    borderRadius: 18,
    padding: 16,
    background: "#f8fafc",
  },
  suggestionTop: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  badge: {
    background: "#eef2ff",
    color: "#4f33e8",
    borderRadius: 999,
    padding: "5px 9px",
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  priority: {
    background: "#ecfdf5",
    color: "#059669",
    borderRadius: 999,
    padding: "5px 9px",
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  suggestionTitle: {
    margin: "0 0 8px",
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 950,
  },
  suggestionDetail: {
    margin: "0 0 14px",
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 650,
  },
  empty: {
    background: "#f8fafc",
    border: "1px solid #e6edf5",
    color: "#64748b",
    borderRadius: 18,
    padding: 18,
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  emptySmall: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },
  resultGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 18,
  },
  resultBlock: {
    background: "#f8fafc",
    border: "1px solid #e6edf5",
    borderRadius: 18,
    padding: 18,
  },
  resultBlockWide: {
    background: "#f8fafc",
    border: "1px solid #e6edf5",
    borderRadius: 18,
    padding: 18,
    gridColumn: "1 / -1",
  },
  resultHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10,
  },
  resultTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 15,
    fontWeight: 950,
  },
  resultText: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    fontWeight: 650,
  },
  ul: {
    margin: 0,
    paddingLeft: 18,
  },
  li: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.55,
    marginBottom: 6,
    fontWeight: 650,
  },
  errorBox: {
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
    borderRadius: 18,
    padding: 16,
    marginBottom: 24,
  },
  errorPre: {
    margin: "8px 0 0",
    whiteSpace: "pre-wrap",
    fontSize: 12,
  },
};